import React, { useState, useEffect } from "react";
import MainPageDialog from "../../../../../components/dialog";
import { InputField } from "../../../../../components/reactHookFormFields/inputField";
import { MaskField } from "../../../../../components/reactHookFormFields/maskField";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useAddNurseMutation, useUpdateNurseMutation,  } from "../../../../../services/location_services";
import { formatPhoneNumber } from "../../../../../utils/common_lib";

const getPhoneDigits = (phone = "") => String(phone || "").replace(/\D/g, "");

function AddFacilityStaffDialog({
  setShowAddDialog = () => {},
  refetchNurses = () => {},
  selectedNurse = null,
  setSelectedNurse = () => {},
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addNurse] = useAddNurseMutation();
    const [updateNurseToLocation] = useUpdateNurseMutation();
    
    const isEditMode = !!selectedNurse;
    
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        getValues,
        trigger,
    } = useForm({
        defaultValues: {
            name: "",
            pincode: "",
            phone: "",
            username: "",
            password: "",
            email: "",
        },
    });

    // Pre-populate form when editing
    useEffect(() => {
        if (isEditMode && selectedNurse) {
            setValue("name", selectedNurse?.name || "");
            setValue("pincode", selectedNurse?.pincode || ""); // Don't pre-fill PIN for security
            setValue("phone", formatPhoneNumber(selectedNurse?.phone || selectedNurse?.phone || "") || "");
            setValue("username", selectedNurse?.username || "");
            setValue("password", ""); // Don't pre-fill password for security
            setValue("email", selectedNurse?.email || "");
        } else {
            reset({
                name: "",
                pincode: "",
                phone: "",
                username: "",
                password: "",
                email: "",
            });
        }
    }, [isEditMode, selectedNurse, setValue, reset]);

    const handleCloseDialog = () => {
        setShowAddDialog(false);
        setSelectedNurse(null);
        reset({
            name: "",
            pin: "",
            phone: "",
            username: "",
            password: "",
            email: "",
        });
    };

    const onSubmitForm = async (values) => {
        setIsSubmitting(true);
        try {
            let response;
            if (isEditMode) {
                const updateData = {
                    id: selectedNurse?.id,
                    name: values.name,
                    phone: values.phone,
                    username: values.username,
                    email: values.email,
                    pincode: values.pincode,
                };
                
                // Only include password if they were provided
                if (values.password) {
                    updateData.password = values.password;
                }
                
                response = await updateNurseToLocation({
                    body: updateData
                }).unwrap();
                if (response?.code == 0) {
                    toast.success("Facility staff updated successfully");
                    handleCloseDialog();
                    refetchNurses();
                }
            } else {
                // Add new nurse
                response = await addNurse({
                    body: {
                        name: values.name,
                        pincode: values.pincode,
                        phone: values.phone,
                        username: values.username,
                        password: values.password,
                        email: values.email,
                    },
                }).unwrap();

                if (response?.code == 0) {
                    toast.success("Facility staff added successfully");
                    handleCloseDialog();
                    refetchNurses();
                }
            }
        } catch (error) {
            toast.error(error?.data?.msg || `Failed to ${isEditMode ? 'update' : 'add'} facility staff`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const footerList = [
        {
            title: "Close",
            action: handleCloseDialog,
            type: "cancel",
            disabled: isSubmitting,
        },
        {
            title: isEditMode ? "Update" : "Save",
            action: () => handleSubmit(onSubmitForm)(),
            type: isEditMode ? "update" : "save",
            disabled: isSubmitting,
            isDisabled: isSubmitting,
            loading: isSubmitting,
        },
    ];

    return (
        <MainPageDialog
            title={isEditMode ? "Edit Nurse" : "Select Nurse"}
            footerList={footerList}
            onCancelHandler={handleCloseDialog}
            modalSize="md"
        >
        <div className="flex flex-col w-full my-3 min-w-0">
            <form
                id="add_facility_staff_form"
                onSubmit={handleSubmit(onSubmitForm)}
                className="flex flex-col gap-4"
            >
                {/* Row 1 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 items-start">
                    <div className="min-w-0">
                        <InputField
                            name="name"
                            id="name"
                            aria-label="Nurse Name"
                            title="Enter Nurse Name"
                            control={control}
                            errors={errors}
                            autoComplete="off"
                            rules={{ required: { value: true, message: "Nurse name is required" } }}
                        />
                    </div>
                    <div className="min-w-0">
                        <InputField
                            name="pincode"
                            id="pincode"
                            type="text"
                            aria-label="PIN"
                            title="Please enter PIN"
                            control={control}
                            errors={errors}
                            autoComplete="off"
                            onChange={(e) => {
                                const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 4);
                                if (digitsOnly !== e.target.value) {
                                    setValue("pincode", digitsOnly);
                                }
                            }}
                            rules={{
                                required: {
                                    value: true,
                                    message: "Please enter 4 digit PIN",
                                },
                                minLength: {
                                    value: 4,
                                    message: "PIN must be 4 digits",
                                },
                                maxLength: {
                                    value: 4,
                                    message: "PIN must be 4 digits",
                                },
                            }}
                        />
                    </div>
                    <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                        <MaskField
                            name="phone"
                            id="phone"
                            mask="(999) 999-9999"
                            aria-label="Mobile Number"
                            title="Enter Mobile Number"
                            control={control}
                            errors={errors}
                            autoComplete="off"
                            rules={{
                                validate: {
                                    required: (value) =>
                                        getPhoneDigits(value).length || getValues("email")
                                            ? true
                                            : "Please enter either phone number or email",
                                    validPhone: (value) => {
                                        const digits = getPhoneDigits(value);

                                        return !digits.length || digits.length === 10
                                            ? true
                                            : "Please enter a valid phone number";
                                    },
                                },
                            }}
                            onChange={() => trigger("email")}
                        />
                    </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 items-start">
                    <div className="min-w-0">
                        <InputField
                            name="username"
                            id="username"
                            aria-label="Username"
                            title="Enter Username"
                            control={control}
                            errors={errors}
                            autoComplete="off"
                            rules={{ required: { value: true, message: "Username is required" } }}
                        />
                    </div>
                    <div className="min-w-0">
                        <InputField
                            name="password"
                            id="password"
                            type="password"
                            aria-label="Password"
                            title="Enter Password"
                            control={control}
                            errors={errors}
                            autoComplete="off"
                            rules={{
                                required: { value: isEditMode ? false : true, message: "Password is required" },
                                pattern: {
                                    value: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}$/,
                                    message: "Must include 1 uppercase (A), 1 digit (0-9), and be 8 characters long.",
                                },
                            }}
                        />
                    </div>
                    <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                        <InputField
                            name="email"
                            id="email"
                            type="email"
                            aria-label="Email"
                            title="Enter Email"
                            control={control}
                            errors={errors}
                            autoComplete="off"
                            rules={{
                                validate: {
                                    required: (value) =>
                                        value || getPhoneDigits(getValues("phone")).length
                                            ? true
                                            : "Please enter either email or phone number",
                                    validEmail: (value) =>
                                        !value || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)
                                            ? true
                                            : "Invalid email address",
                                },
                            }}
                            onChange={() => trigger("phone")}
                        />
                    </div>
                </div>
            </form>
        </div>
        </MainPageDialog>
    );
}

export default AddFacilityStaffDialog;